import { useTranslation } from "@sagot/i18n";
import { Button, Icon, type IconName } from "@sagot/ui";
import { isEditable, type Announcement, type DisplayStatus } from "./model";

export type CardAction = "send" | "duplicate" | "archive" | "unarchive" | "delete";

export interface CardHandlers {
  canEdit: boolean;
  canSend: boolean;
  pendingId: string | null;
  onAction: (item: Announcement, action: CardAction) => void;
  onEdit: (item: Announcement) => void;
}

interface ActionSpec {
  key: CardAction | "edit";
  icon: IconName;
  label: string;
  variant: "primary" | "secondary" | "ghost";
}

function specsFor(status: DisplayStatus, canSend: boolean): ActionSpec[] {
  const dup: ActionSpec = { key: "duplicate", icon: "copy", label: "duplicate", variant: "ghost" };
  if (status === "archived") return [dup, { key: "unarchive", icon: "rotate-ccw", label: "restore", variant: "ghost" }];
  if (status === "draft") {
    return [{ key: "edit", icon: "pencil", label: "continueEditing", variant: "primary" }, dup, { key: "delete", icon: "trash", label: "delete", variant: "ghost" }];
  }
  const edit: ActionSpec = { key: "edit", icon: "pencil", label: "edit", variant: "ghost" };
  const archive: ActionSpec = { key: "archive", icon: "package", label: "archive", variant: "ghost" };
  if (status === "scheduled") {
    const send: ActionSpec[] = canSend ? [{ key: "send", icon: "send", label: "sendNow", variant: "secondary" }] : [];
    return [...send, dup, edit, archive];
  }
  return [dup, archive];
}

/** Per-status card buttons (sent: duplicate/archive · scheduled: send now/edit · draft: continue/delete). */
export function CardActions({ item, status, canEdit, canSend, pendingId, onAction, onEdit }: CardHandlers & { item: Announcement; status: DisplayStatus }) {
  const { t } = useTranslation();
  if (!canEdit) return null;
  const busy = pendingId === item.id;
  return (
    <div className="mt-3 flex flex-wrap justify-end gap-1">
      {specsFor(status, canSend).map((spec) => (
        <Button
          key={spec.key}
          size="sm"
          variant={spec.variant}
          disabled={busy || (spec.key === "edit" && !isEditable(status))}
          aria-label={`${t(`engage.announcements.actions.${spec.label}`)}: ${item.title}`}
          onClick={() => {
            if (spec.key === "edit") onEdit(item);
            else onAction(item, spec.key);
          }}
        >
          <Icon name={spec.icon} />
          {t(`engage.announcements.actions.${spec.label}`)}
        </Button>
      ))}
    </div>
  );
}
